import { Badge } from "@/components/ui/badge";
import { FileText, Loader2, Trash2 } from "lucide-react";

const statusStyles = {
  uploaded: "bg-slate-700 text-slate-200",
  processing: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  review: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  published: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  failed: "bg-red-500/15 text-red-300 border-red-500/30",
};

export function BatchList({ batches, selectedBatch, onSelect, onDelete, deletingBatchId }) {
  return (
    <aside className="rounded-2xl border border-slate-800 bg-slate-900/80 p-4" data-testid="import-batch-list">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="font-headline text-lg font-semibold text-white">Import batches</h3>
        <Badge className="border-slate-700 bg-slate-800 text-slate-300">{batches.length}</Badge>
      </div>
      <div className="space-y-3">
        {batches.length === 0 ? (
          <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-4 text-sm text-slate-500" data-testid="empty-batch-state">
            No imports yet. Upload a vendor price list to begin.
          </div>
        ) : (
          batches.map((batch) => (
            <div
              key={batch.id}
              role="button"
              tabIndex={0}
              data-testid={`import-batch-item-${batch.id}`}
              onClick={() => onSelect(batch)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") onSelect(batch);
              }}
              className={`w-full cursor-pointer rounded-xl border p-4 text-left transition-colors ${
                selectedBatch?.id === batch.id
                  ? "border-blue-500/60 bg-blue-500/10"
                  : "border-slate-800 bg-slate-950/50 hover:border-slate-700 hover:bg-slate-800/50"
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-slate-800 p-2 text-slate-300">
                  {batch.status === "processing" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="truncate text-sm font-semibold text-slate-100">{batch.original_filename}</div>
                    <button
                      type="button"
                      data-testid={`delete-batch-button-${batch.id}`}
                      aria-label={`Delete ${batch.original_filename}`}
                      disabled={deletingBatchId === batch.id}
                      onClick={(event) => {
                        event.stopPropagation();
                        onDelete(batch);
                      }}
                      className="rounded-lg border border-red-500/20 bg-red-500/10 p-2 text-red-300 transition-colors hover:border-red-400/40 hover:bg-red-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {deletingBatchId === batch.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    </button>
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-slate-400">
                    <Badge className={statusStyles[batch.status] || statusStyles.uploaded}>{batch.status}</Badge>
                    <span>{batch.product_count || 0} products</span>
                    <span>{batch.banner_count || 0} banners</span>
                  </div>
                  {batch.error ? <p className="mt-2 text-xs text-red-300">{batch.error}</p> : null}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </aside>
  );
}
